import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, User, Search } from 'lucide-react';
import { toast } from 'sonner';
import { CargaMasivaButton } from '../components/CargaMasivaButton';

export const UsuariosPage = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentUsuario, setCurrentUsuario] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    cliente_id: '',
    role: 'client',
    telefono: ''
  });

  // Filtrar usuarios basado en el término de búsqueda
  const filteredUsuarios = useMemo(() => {
    if (!searchTerm.trim()) return usuarios;
    const term = searchTerm.toLowerCase();
    return usuarios.filter(usuario => 
      usuario.nombre?.toLowerCase().includes(term) ||
      usuario.email?.toLowerCase().includes(term) ||
      usuario.cliente_nombre?.toLowerCase().includes(term) ||
      usuario.role?.toLowerCase().includes(term) ||
      (usuario.role === 'admin' && 'administrador'.includes(term)) ||
      (usuario.role === 'client' && 'cliente'.includes(term)) ||
      usuario.telefono?.toLowerCase().includes(term)
    );
  }, [usuarios, searchTerm]);

  useEffect(() => {
    loadUsuarios();
    loadClientes();
  }, []);

  const loadUsuarios = async () => {
    try {
      const response = await api.get('/usuarios');
      setUsuarios(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    }
  };

  const loadClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data);
    } catch (error) {
      console.error('Error al cargar clientes');
    }
  };

  const handleOpenDialog = (usuario = null) => {
    if (usuario) {
      setCurrentUsuario(usuario);
      setFormData({
        nombre: usuario.nombre,
        email: usuario.email,
        password: '',
        cliente_id: usuario.cliente_id || '',
        role: usuario.role,
        telefono: usuario.telefono || ''
      });
    } else {
      setCurrentUsuario(null);
      setFormData({ nombre: '', email: '', password: '', cliente_id: '', role: 'client', telefono: '' });
    }
    setShowDialog(true);
  };

  const handleRoleChange = (role) => {
    setFormData({ 
      ...formData, 
      role,
      // Limpiar cliente_id si es admin
      cliente_id: role === 'admin' ? '' : formData.cliente_id
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validar que si es cliente, debe tener un cliente asignado
    if (formData.role === 'client' && !formData.cliente_id) {
      toast.error('Debe seleccionar un cliente para usuarios de tipo Cliente');
      setLoading(false);
      return;
    }

    // Validar formato de teléfono: +569XXXXXXXX
    if (formData.telefono && !/^\+569\d{8}$/.test(formData.telefono)) {
      toast.error('El teléfono debe tener el formato +569XXXXXXXX');
      setLoading(false);
      return;
    }

    try {
      const dataToSend = { ...formData };
      // Si es admin, no enviar cliente_id
      if (dataToSend.role === 'admin') {
        delete dataToSend.cliente_id;
      }
      
      if (currentUsuario) {
        if (!dataToSend.password) {
          delete dataToSend.password;
        }
        await api.put(`/usuarios/${currentUsuario.id}`, dataToSend);
        toast.success('Usuario actualizado');
      } else {
        await api.post('/usuarios', dataToSend);
        toast.success('Usuario creado');
      }
      await loadUsuarios();
      setShowDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/usuarios/${currentUsuario.id}`);
      toast.success('Usuario eliminado');
      await loadUsuarios();
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Error al eliminar usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Usuarios</h1>
          <p className="text-gray-600 mt-1">Gestión de usuarios del sistema</p>
        </div>
        <div className="flex gap-2">
          <CargaMasivaButton
            endpoint="/usuarios/carga-masiva"
            onSuccess={loadUsuarios}
            templateColumns={['nombre', 'email', 'password', 'role', 'telefono', 'cliente']}
            templateName="usuarios"
            entityName="Usuarios"
          />
          <Button onClick={() => handleOpenDialog()} data-testid="create-usuario-button">
            <Plus size={18} className="mr-2" />
            Nuevo Usuario
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          {/* Buscador */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar por nombre, email, cliente, rol o teléfono..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-usuarios-input"
              />
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-500 mt-2">
                {filteredUsuarios.length} resultado{filteredUsuarios.length !== 1 ? 's' : ''} encontrado{filteredUsuarios.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsuarios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                    {searchTerm ? 'No se encontraron usuarios con ese criterio' : 'No hay usuarios registrados'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsuarios.map((usuario) => (
                  <TableRow key={usuario.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-primary" />
                        {usuario.nombre}
                      </div>
                    </TableCell>
                    <TableCell>{usuario.email}</TableCell>
                    <TableCell>
                      {usuario.role === 'admin' ? (
                        <span className="text-gray-400 italic">N/A (Admin)</span>
                      ) : (
                        usuario.cliente_nombre || '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={usuario.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                        {usuario.role === 'admin' ? 'Administrador' : 'Cliente'}
                      </Badge>
                    </TableCell>
                    <TableCell>{usuario.telefono || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(usuario)}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setCurrentUsuario(usuario);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="usuario-dialog">
          <DialogHeader>
            <DialogTitle>
              {currentUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
                data-testid="nombre-input"
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Contraseña {currentUsuario ? '(dejar vacío para no cambiar)' : '*'}</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!currentUsuario}
                data-testid="password-input"
              />
            </div>

            <div>
              <Label htmlFor="role">Tipo de Usuario *</Label>
              <Select
                value={formData.role}
                onValueChange={handleRoleChange}
              >
                <SelectTrigger id="role" data-testid="role-select">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="client">Cliente</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.role === 'admin' 
                  ? 'El administrador gestiona la empresa de transporte'
                  : 'El cliente solicita servicios de transporte'}
              </p>
            </div>

            <div>
              <Label htmlFor="cliente" className={formData.role === 'admin' ? 'text-gray-400' : ''}>
                Cliente {formData.role === 'client' ? '*' : ''}
              </Label>
              <Select
                value={formData.cliente_id}
                onValueChange={(value) => setFormData({ ...formData, cliente_id: value })}
                disabled={formData.role === 'admin'}
              >
                <SelectTrigger 
                  id="cliente" 
                  data-testid="cliente-select"
                  className={formData.role === 'admin' ? 'opacity-50 cursor-not-allowed' : ''}
                >
                  <SelectValue placeholder={formData.role === 'admin' ? 'No aplica para administradores' : 'Seleccionar cliente'} />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.role === 'client' && (
                <p className="text-xs text-gray-500 mt-1">
                  El cliente es la empresa a la que pertenece este usuario
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="telefono">Teléfono</Label>
              <Input
                id="telefono"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                placeholder="+569XXXXXXXX"
                data-testid="telefono-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="submit-usuario-button">
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Eliminación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar al usuario <strong>{currentUsuario?.nombre}</strong>?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
